import React, { useState } from 'react';
import {
  Clock, Shield, Edit, ChevronDown, CheckCircle, X,
  AlertCircle, FileText, ArrowRight
} from 'lucide-react';
import BusinessUnitName from './BusinessUnitName';
import PutawayDialog from './PutawayDialog';
import TruckNotesThread from './TruckNotesThread';
import { formatTime, formatDateTime } from './utils/dateUtils';
import { countRefList, formatRefListCompact, formatRefListFull } from './utils/refListUtils';
import { useTimezone } from '../../../../hooks/useTimezone';

/**
 * Reusable expandable truck tile component with actions
 */
const TruckTile = ({
  truck,
  borderColor = 'border-slate-600',
  statusBgColor = 'bg-blue-900/30',
  statusTextColor = 'text-blue-300',
  statusBorderColor = 'border-blue-500/20',
  openTruckEditor,
  onStatusChange,
  onLogiwaComplete,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [confirmationAction, setConfirmationAction] = useState(null);
  
  // Get user's timezone
  const userTimezone = useTimezone();
  
  // Determine the next status based on current status for inbound/outbound trucks
  const getNextStatus = () => {
    // Check for outbound-specific fields to determine if this is an outbound truck
    const isOutbound = 'customer' in truck || 'staging_area' in truck;

    if (isOutbound) {
      // Outbound status progression based on DB schema
      // Status enum('Scheduled','Staged','Loaded','Shipped','Completed','Cancelled')
      switch (truck.status) {
        case 'Scheduled': return 'Staged';
        case 'Staged': return 'Loaded';
        case 'Loaded': return 'Shipped';
        case 'Shipped': return 'Completed';
        default: return null;
      }
    } else {
      // Inbound status progression
      switch (truck.status) {
        case 'Scheduled': return 'Arrived';
        case 'Arrived': return 'Received';
        case 'Received': return 'Putaway';
        case 'Putaway': return 'Completed';
        default: return null;
      }
    }
  };

  // Get the appropriate label for the next status button
  const getNextStatusLabel = () => {
    const nextStatus = getNextStatus();
    if (!nextStatus) return null;

    const isOutbound = 'customer' in truck || 'staging_area' in truck;
    if (isOutbound) {
      // Use clear action language while following database schema status values
      switch (nextStatus) {
        case 'Staged': return 'Mark as Staged';
        case 'Loaded': return 'Mark as Loaded';
        case 'Shipped': return 'Mark as Shipped';
        case 'Completed': return 'Mark as Completed';
        default: return `Mark as ${nextStatus}`;
      }
    } else {
      switch (nextStatus) {
        case 'Arrived': return 'Mark as Arrived';
        case 'Received': return 'Start Receiving';
        case 'Putaway': return 'Ready for Putaway';
        case 'Completed': return 'Complete Putaway';
        default: return `Mark as ${nextStatus}`;
      }
    }
  };

  // Get icon for the next status action
  const getNextStatusIcon = () => {
    const nextStatus = getNextStatus();
    if (!nextStatus) return null;
    
    switch (nextStatus) {
      case 'Arrived': return CheckCircle;
      case 'Received': return ArrowRight;
      case 'Putaway': return ArrowRight;
      case 'Staged': return ArrowRight;
      case 'Loaded': return ArrowRight;
      case 'Shipped': return ArrowRight;
      case 'Completed': return CheckCircle;
      default: return CheckCircle;
    }
  };
  
  // State for confirmation dialog
  const [showPutawayDialog, setShowPutawayDialog] = useState(false);

  // Handle putaway confirmation specifically
  const handlePutawayConfirm = (truckId, status, data) => {
    console.log('Putaway confirmed with data:', data);
    if (onStatusChange) {
      onStatusChange(truckId, status, data);
    }
    setShowPutawayDialog(false);
  };

  // Function to handle status change with confirmation
  const handleStatusChange = (newStatus) => {
    // Use our specialized putaway dialog for Putaway status
    if (truck.status === 'Received' && newStatus === 'Putaway') {
      console.log('Opening dedicated putaway dialog');
      setShowPutawayDialog(true);
      return;
    }

    // For the Shipped to Completed transition in outbound mode, branch on whether the
    // truck is LTL (the default — anything not explicitly marked Small Parcel) or small-parcel.
    // LTL trucks go through the Logiwa-aware Complete wizard instead of the standard
    // StatusChangeConfirmation flow.
    const isOutbound = 'customer' in truck || 'staging_area' in truck;
    if (isOutbound && truck.status === 'Shipped' && newStatus === 'Completed') {
      const isLtl = !truck.is_small_parcel;
      if (isLtl && onLogiwaComplete) {
        console.log('Routing LTL Complete to LogiwaCompleteWizard');
        onLogiwaComplete(truck);
        return;
      }
      console.log('Using StatusChangeConfirmation modal for BOL upload');
      // Use the main status change confirmation modal from the parent
      if (openTruckEditor) {
        // Pass status change as props to the parent to handle with the StatusChangeConfirmation
        openTruckEditor(truck, {
          statusChange: {
            direction: 'forward',
            fromStatus: { key: 'Shipped', label: 'Shipped' },
            toStatus: { key: 'Completed', label: 'Completed' }
          }
        });
        return;
      }
    }

    // For other status changes, use the general confirmation dialog
    setConfirmationAction({
      currentStatus: truck.status,
      newStatus: newStatus,
      execute: () => {
        if (onStatusChange) {
          onStatusChange(truck.id, newStatus);
        }
        setConfirmationAction(null);
      },
      cancel: () => {
        // Just clear the confirmation
        setConfirmationAction(null);
      }
    });
  };
  
  // Next status button and icon
  const nextStatus = getNextStatus();
  const nextStatusLabel = getNextStatusLabel();
  const StatusIcon = getNextStatusIcon();
  
  // Check if this is an outbound truck by looking for outbound-specific fields
  const isOutbound = 'customer' in truck || 'staging_area' in truck;

  return (
    <div className={`bg-slate-700 rounded-lg p-3 hover:bg-slate-650 border ${borderColor}`}>
      {/* Main clickable content for expanding/collapsing */}
      <div 
        className="cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex justify-between items-start mb-2">
          <div>
            <div className="font-medium text-white">{truck.carrier}</div>
            <div className="text-xs text-slate-300"><BusinessUnitName id={truck.business_unit} /></div>
            {truck.conf_number && (
              <div className="text-xs text-slate-400 mt-0.5">
                Conf #: <span className="text-slate-200 font-medium">{truck.conf_number}</span>
              </div>
            )}
          </div>
          <div className="flex items-center space-x-2">
            {/* Multi-order trucks carry comma-separated lists here. Collapse to "first +N" so a
                5-order list doesn't blow out the tile header; full list is in the tooltip and
                in the expanded detail below. */}
            {truck.po && (
              <div
                className={`text-xs px-2 py-1 ${statusBgColor} ${statusTextColor} rounded border ${statusBorderColor} whitespace-nowrap`}
                title={formatRefListFull(truck.po)}
              >
                PO: {formatRefListCompact(truck.po)}
              </div>
            )}
            {truck.order_number && (
              <div
                className={`text-xs px-2 py-1 ${statusBgColor} ${statusTextColor} rounded border ${statusBorderColor} whitespace-nowrap`}
                title={formatRefListFull(truck.order_number)}
              >
                Order: {formatRefListCompact(truck.order_number)}
              </div>
            )}
            <button 
              className="p-1 bg-slate-600 hover:bg-slate-500 rounded text-slate-300"
              onClick={(e) => {
                e.stopPropagation();
                openTruckEditor && openTruckEditor(truck);
              }}
              title="Edit truck"
            >
              <Edit className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
        
        {/* Quick summary info */}
        <div className="flex items-center text-xs text-slate-400 flex-wrap gap-y-1">
          <div className="flex items-center mr-2">
            <Clock className="w-3 h-3 mr-1" />
            {formatTime(truck.scheduled_at, 'h:mm a', userTimezone)}
          </div>
          {!isOutbound && truck.vendor && (
            <div className="mr-2 text-slate-300">
              Vendor: {truck.vendor}
            </div>
          )}
          {truck.load_type && (
            <div className="mr-2 px-1.5 py-0.5 bg-slate-600 rounded text-slate-300">
              {truck.load_type}
            </div>
          )}
          {truck.load_material && (
            <div className="mr-2 px-1.5 py-0.5 bg-slate-600/50 rounded text-slate-300">
              {truck.load_material}
            </div>
          )}
          {truck.size && (
            <div className="mr-2">
              {truck.size}
            </div>
          )}
          {truck.dock_door && (
            <div className="mr-2">
              Dock {truck.dock_door}
            </div>
          )}
          {truck.secure === 1 && (
            <div className="text-red-400 flex items-center">
              <Shield className="w-3 h-3 mr-1" />
              Secure
            </div>
          )}
          {isOutbound && truck.customer && (
            <div className="mr-2">
              Customer: {truck.customer}
            </div>
          )}
        </div>
        
        {/* Expand/collapse indicator */}
        <div className="mt-1 text-xs text-slate-500 flex items-center">
          <span>{isExpanded ? "Hide details" : "View details"}</span>
          <ChevronDown className={`w-3.5 h-3.5 ml-0.5 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
        </div>
      </div>
      
      {/* Expanded detail section */}
      {isExpanded && (
        <div className="mt-3 pt-3 border-t border-slate-600">
          <div className="space-y-3">
            {/* Detailed Information */}
            <div className="grid grid-cols-2 gap-2 text-sm">
              {truck.conf_number && (
                <div>
                  <div className="text-slate-400 text-xs">Confirmation Number</div>
                  <div className="text-slate-200 font-medium">{truck.conf_number}</div>
                </div>
              )}
              <div>
                <div className="text-slate-400 text-xs">Carrier</div>
                <div className="text-slate-200">{truck.carrier}</div>
              </div>
              <div>
                <div className="text-slate-400 text-xs">Business Unit</div>
                <div className="text-slate-200"><BusinessUnitName id={truck.business_unit} /></div>
              </div>
              <div>
                <div className="text-slate-400 text-xs">Status</div>
                <div className={`${statusTextColor}`}>{truck.status}</div>
              </div>
              <div>
                <div className="text-slate-400 text-xs">Scheduled</div>
                <div className="text-slate-200">{formatDateTime(truck.scheduled_at, 'MMM dd, yyyy h:mm a', userTimezone)}</div>
              </div>
              <div>
                <div className="text-slate-400 text-xs">Load Type</div>
                <div className="text-slate-200">{truck.load_type}</div>
              </div>
              <div>
                <div className="text-slate-400 text-xs">Load Material</div>
                <div className="text-slate-200">{truck.load_material || "—"}</div>
              </div>
              <div>
                <div className="text-slate-400 text-xs">Size</div>
                <div className="text-slate-200">{truck.size} </div>
              </div>
              {/* Expanded view shows the full list — this is where an operator goes to read all
                  the POs on a multi-order truck, so wrap rather than truncate. */}
              {truck.po && (
                <div>
                  <div className="text-slate-400 text-xs">
                    {countRefList(truck.po) > 1 ? `PO Numbers (${countRefList(truck.po)})` : 'PO Number'}
                  </div>
                  <div className="text-slate-200 break-words">{formatRefListFull(truck.po)}</div>
                </div>
              )}
              {truck.order_number && (
                <div>
                  <div className="text-slate-400 text-xs">
                    {countRefList(truck.order_number) > 1
                      ? `Order Numbers (${countRefList(truck.order_number)})`
                      : 'Order Number'}
                  </div>
                  <div className="text-slate-200 break-words">{formatRefListFull(truck.order_number)}</div>
                </div>
              )}
              {truck.dock_door && (
                <div>
                  <div className="text-slate-400 text-xs">Dock Door</div>
                  <div className="text-slate-200">{truck.dock_door}</div>
                </div>
              )}
              <div>
                <div className="text-slate-400 text-xs">Security</div>
                <div className="text-slate-200">{truck.secure === 1 ? 'Secure Load' : 'Standard'}</div>
              </div>
              
              {/* Inbound specific fields */}
              {!isOutbound && truck.vendor && (
                <div>
                  <div className="text-slate-400 text-xs">Vendor</div>
                  <div className="text-slate-200">{truck.vendor}</div>
                </div>
              )}
              
              {/* Outbound specific fields */}
              {isOutbound && truck.customer && (
                <div>
                  <div className="text-slate-400 text-xs">Customer</div>
                  <div className="text-slate-200">{truck.customer}</div>
                </div>
              )}
              {isOutbound && truck.staging_area && (
                <div>
                  <div className="text-slate-400 text-xs">Staging Area</div>
                  <div className="text-slate-200">{truck.staging_area}</div>
                </div>
              )}
              {isOutbound && truck.destination && (
                <div>
                  <div className="text-slate-400 text-xs">Destination</div>
                  <div className="text-slate-200">{truck.destination}</div>
                </div>
              )}
              {isOutbound && truck.priority && (
                <div>
                  <div className="text-slate-400 text-xs">Priority</div>
                  <div className="text-slate-200">{truck.priority}</div>
                </div>
              )}
            </div>
            
            {/* Notes Thread */}
            <div>
              <div className="text-slate-400 text-xs mb-1">Notes</div>
              <TruckNotesThread
                truckType={isOutbound ? 'outbound' : 'inbound'}
                truckId={truck.id}
                mode="edit"
                theme="dark"
                emptyMessage="No notes yet."
              />
            </div>
            
            {/* Status Timeline */}
            <div>
              <div className="text-slate-400 text-xs mb-2">Status Timeline</div>
              <div className="relative pl-5 border-l border-slate-600 space-y-2">
                <div>
                  <div className="absolute left-0 w-2.5 h-2.5 rounded-full bg-blue-500 -translate-x-1/2 mt-0.5"></div>
                  <div className="text-slate-300 text-xs">Scheduled</div>
                  <div className="text-slate-400 text-xs">{formatDateTime(truck.scheduled_at, 'MMM dd, yyyy h:mm a', userTimezone)}</div>
                </div>

                {/* Inbound timeline events */}
                {!isOutbound && truck.arrived_at && (
                  <div>
                    <div className="absolute left-0 w-2.5 h-2.5 rounded-full bg-green-500 -translate-x-1/2 mt-0.5"></div>
                    <div className="text-slate-300 text-xs">Arrived</div>
                    <div className="text-slate-400 text-xs">{formatDateTime(truck.arrived_at, 'MMM dd, yyyy h:mm a', userTimezone)}</div>
                  </div>
                )}
                {!isOutbound && truck.received_at && (
                  <div>
                    <div className="absolute left-0 w-2.5 h-2.5 rounded-full bg-purple-500 -translate-x-1/2 mt-0.5"></div>
                    <div className="text-slate-300 text-xs">Received</div>
                    <div className="text-slate-400 text-xs">{formatDateTime(truck.received_at, 'MMM dd, yyyy h:mm a', userTimezone)}</div>
                  </div>
                )}
                {!isOutbound && truck.putaway_at && (
                  <div>
                    <div className="absolute left-0 w-2.5 h-2.5 rounded-full bg-indigo-500 -translate-x-1/2 mt-0.5"></div>
                    <div className="text-slate-300 text-xs">Putaway</div>
                    <div className="text-slate-400 text-xs">{formatDateTime(truck.putaway_at, 'MMM dd, yyyy h:mm a', userTimezone)}</div>
                  </div>
                )}

                {/* Outbound timeline events */}
                {isOutbound && truck.staged_at && (
                  <div>
                    <div className="absolute left-0 w-2.5 h-2.5 rounded-full bg-green-500 -translate-x-1/2 mt-0.5"></div>
                    <div className="text-slate-300 text-xs">Staged</div>
                    <div className="text-slate-400 text-xs">{formatDateTime(truck.staged_at, 'MMM dd, yyyy h:mm a', userTimezone)}</div>
                  </div>
                )}
                {isOutbound && truck.loaded_at && (
                  <div>
                    <div className="absolute left-0 w-2.5 h-2.5 rounded-full bg-purple-500 -translate-x-1/2 mt-0.5"></div>
                    <div className="text-slate-300 text-xs">Loaded</div>
                    <div className="text-slate-400 text-xs">{formatDateTime(truck.loaded_at, 'MMM dd, yyyy h:mm a', userTimezone)}</div>
                  </div>
                )}
                {isOutbound && truck.shipped_at && (
                  <div>
                    <div className="absolute left-0 w-2.5 h-2.5 rounded-full bg-indigo-500 -translate-x-1/2 mt-0.5"></div>
                    <div className="text-slate-300 text-xs">Shipped</div>
                    <div className="text-slate-400 text-xs">{formatDateTime(truck.shipped_at, 'MMM dd, yyyy h:mm a', userTimezone)}</div>
                  </div>
                )}

                {/* Completed status for both types */}
                {truck.completed_at && (
                  <div>
                    <div className="absolute left-0 w-2.5 h-2.5 rounded-full bg-green-500 -translate-x-1/2 mt-0.5"></div>
                    <div className="text-slate-300 text-xs">Completed</div>
                    <div className="text-slate-400 text-xs">{formatDateTime(truck.completed_at, 'MMM dd, yyyy h:mm a', userTimezone)}</div>
                  </div>
                )}

                {/* Cancelled status (if applicable) */}
                {truck.cancelled_at && (
                  <div>
                    <div className="absolute left-0 w-2.5 h-2.5 rounded-full bg-red-500 -translate-x-1/2 mt-0.5"></div>
                    <div className="text-slate-300 text-xs">Cancelled</div>
                    <div className="text-slate-400 text-xs">{formatDateTime(truck.cancelled_at, 'MMM dd, yyyy h:mm a', userTimezone)}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons for Status Transitions */}
            {truck.status !== 'Completed' && truck.status !== 'Cancelled' && (
              <div className="pt-2 border-t border-slate-600">
                <div className="text-slate-400 text-xs mb-2">Actions</div>
                <div className="flex flex-wrap gap-2">
                  {nextStatus && (
                    <button
                      className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-sm rounded flex items-center"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStatusChange(nextStatus);
                      }}
                    >
                      {StatusIcon && <StatusIcon className="w-4 h-4 mr-1.5" />}
                      {nextStatusLabel}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Regular Confirmation Modal */}
      {confirmationAction && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={(e) => e.stopPropagation()}>
          <div className="bg-slate-800 rounded-lg p-5 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-white mb-3">
              Confirm Status Change
            </h3>
            <p className="text-slate-300 mb-5">
              Are you sure you want to change the status from <span className="font-medium text-blue-400">{confirmationAction.currentStatus}</span> to <span className="font-medium text-green-400">{confirmationAction.newStatus}</span>?
            </p>

            {confirmationAction.newStatus === 'Cancelled' && (
              <div className="mb-5 p-3 bg-red-900/30 border border-red-500/30 rounded">
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-red-400 mr-2 flex-shrink-0 mt-0.5" />
                  <p className="text-red-300 text-sm">
                    This action cannot be undone. Cancelling this truck will remove it from the active workflow.
                  </p>
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <button
                className="px-4 py-2 bg-slate-700 text-slate-300 rounded hover:bg-slate-600"
                onClick={() => confirmationAction.cancel()}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                onClick={() => confirmationAction.execute()}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Special Putaway Dialog */}
      <PutawayDialog
        isOpen={showPutawayDialog}
        onClose={() => setShowPutawayDialog(false)}
        onConfirm={handlePutawayConfirm}
        truckId={truck.id}
      />
    </div>
  );
};

export default TruckTile;